import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { getOwner, type OwnerDetail } from '../owners.api';

export type OwnerRecordState =
  { status: 'loading' } | { status: 'loaded'; owner: OwnerDetail } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): OwnerRecordState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/** One owner, fetched by id and re-fetchable after a mutation commits. */
export function useOwnerRecord(id: string) {
  const [state, setState] = useState<OwnerRecordState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    getOwner(id)
      .then((owner) => {
        if (!cancelled) setState({ status: 'loaded', owner });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', owner: await getOwner(id) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, reload };
}
